/**
 * Styles for @mapbox/mapbox-gl-draw that MapLibre accepts.
 *
 * Draw's built-in theme writes `line-dasharray: [0.2, 2]` as a bare array.
 * MapLibre 4 reads a bare array in a paint property as an expression, so it
 * rejects those layers ("Expression name must be a string") and the lines of
 * the shape being drawn never appear: corners land, edges do not, and the
 * selected-shape layers the trash button relies on are missing too. These are
 * the same layers with the arrays wrapped in ["literal", …].
 */
const BLUE = "#3bb2d0";
const ORANGE = "#fbb03b";
const WHITE = "#ffffff";

// mapbox-gl-draw types its `styles` option as `object[]`.
export const DRAW_STYLES: object[] = [
  {
    id: "gl-draw-polygon-fill-inactive",
    type: "fill",
    filter: [
      "all",
      ["==", "active", "false"],
      ["==", "$type", "Polygon"],
      ["!=", "mode", "static"],
    ],
    paint: { "fill-color": BLUE, "fill-outline-color": BLUE, "fill-opacity": 0.15 },
  },
  {
    id: "gl-draw-polygon-fill-active",
    type: "fill",
    filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
    paint: { "fill-color": ORANGE, "fill-outline-color": ORANGE, "fill-opacity": 0.15 },
  },
  {
    id: "gl-draw-polygon-midpoint",
    type: "circle",
    filter: ["all", ["==", "$type", "Point"], ["==", "meta", "midpoint"]],
    paint: { "circle-radius": 4, "circle-color": ORANGE },
  },
  {
    id: "gl-draw-polygon-stroke-inactive",
    type: "line",
    filter: [
      "all",
      ["==", "active", "false"],
      ["==", "$type", "Polygon"],
      ["!=", "mode", "static"],
    ],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": BLUE, "line-width": 2.5 },
  },
  {
    id: "gl-draw-polygon-stroke-active",
    type: "line",
    filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": ORANGE,
      "line-dasharray": ["literal", [0.2, 2]],
      "line-width": 2.5,
    },
  },
  {
    id: "gl-draw-line-inactive",
    type: "line",
    filter: [
      "all",
      ["==", "active", "false"],
      ["==", "$type", "LineString"],
      ["!=", "mode", "static"],
    ],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: { "line-color": BLUE, "line-width": 2.5 },
  },
  {
    id: "gl-draw-line-active",
    type: "line",
    filter: ["all", ["==", "$type", "LineString"], ["==", "active", "true"]],
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": ORANGE,
      "line-dasharray": ["literal", [0.2, 2]],
      "line-width": 2.5,
    },
  },
  {
    id: "gl-draw-polygon-and-line-vertex-stroke-inactive",
    type: "circle",
    filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["!=", "mode", "static"]],
    paint: { "circle-radius": 6, "circle-color": WHITE },
  },
  {
    id: "gl-draw-polygon-and-line-vertex-inactive",
    type: "circle",
    filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["!=", "mode", "static"]],
    paint: { "circle-radius": 4, "circle-color": ORANGE },
  },
  {
    id: "gl-draw-point-point-stroke-inactive",
    type: "circle",
    filter: [
      "all",
      ["==", "active", "false"],
      ["==", "$type", "Point"],
      ["==", "meta", "feature"],
      ["!=", "mode", "static"],
    ],
    paint: { "circle-radius": 5, "circle-opacity": 1, "circle-color": WHITE },
  },
  {
    id: "gl-draw-point-inactive",
    type: "circle",
    filter: [
      "all",
      ["==", "active", "false"],
      ["==", "$type", "Point"],
      ["==", "meta", "feature"],
      ["!=", "mode", "static"],
    ],
    paint: { "circle-radius": 3, "circle-color": BLUE },
  },
  {
    id: "gl-draw-point-stroke-active",
    type: "circle",
    filter: ["all", ["==", "$type", "Point"], ["==", "active", "true"], ["!=", "meta", "midpoint"]],
    paint: { "circle-radius": 7, "circle-color": WHITE },
  },
  {
    id: "gl-draw-point-active",
    type: "circle",
    filter: ["all", ["==", "$type", "Point"], ["!=", "meta", "midpoint"], ["==", "active", "true"]],
    paint: { "circle-radius": 5, "circle-color": ORANGE },
  },
];

/**
 * Give mapbox-gl-draw's buttons MapLibre's control classes.
 *
 * Draw tags its button group `mapboxgl-ctrl mapboxgl-ctrl-group`. MapLibre's
 * corner container is `pointer-events: none` and turns events back on only
 * for `.maplibregl-ctrl` children, so with Draw's own names the canvas takes
 * every click: the polygon and trash buttons show but cannot be pressed.
 * Draw reads these names when it builds its controls, so this must run
 * before `new MapboxDraw(...)`.
 */
export function applyMapLibreClassesToDraw(draw: { constants?: unknown }): void {
  const classes = (draw.constants as { classes?: Record<string, string> } | undefined)?.classes;
  if (!classes) return;
  classes.CONTROL_BASE = "maplibregl-ctrl";
  classes.CONTROL_PREFIX = "maplibregl-ctrl-";
  classes.CONTROL_GROUP = "maplibregl-ctrl-group";
}
